import {
    useOptionalRouteLevelContext,
    useOptionalRouterContext,
    useRouterContext,
    useRouterNavigation,
} from './context';
import { useCallback, useMemo } from 'react';
import { useContainer } from 'src/features/ioc';
import { hasNavigationPathname, resolvePath, toLocation } from 'src/lib/navigation/path';
import type { AppLocation, NavigationTarget, SetURLSearchParams } from 'src/lib/navigation/types';
import type { RouteParams } from 'src/lib/router/core';

export const useLocation = (): AppLocation => useRouterContext().snapshot.location;

export function useParams<TKey extends string = string>(): Readonly<Record<TKey, string | undefined>>;
export function useParams<TParams extends Record<string, string | undefined>>(): Readonly<Partial<TParams>>;
export function useParams(): RouteParams {
    return useRouterContext().snapshot.params;
}

export const useSearchParams = (): readonly [URLSearchParams, SetURLSearchParams] => {
    const location = useLocation();
    const container = useContainer();
    const contextNavigation = useRouterNavigation();
    const navigation = contextNavigation ?? container.getOptional('NavigationService');
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const setParams = useCallback<SetURLSearchParams>(
        (nextInit, options) => {
            if (!navigation) return;
            const next =
                typeof nextInit === 'function'
                    ? nextInit(new URLSearchParams(navigation.location.value.search))
                    : nextInit instanceof URLSearchParams
                      ? nextInit
                      : new URLSearchParams(nextInit);
            void navigation.navigate(
                {
                    hash: navigation.location.value.hash,
                    pathname: navigation.location.value.pathname,
                    search: next.size > 0 ? `?${next.toString()}` : '',
                },
                options,
            );
        },
        [navigation],
    );

    return [params, setParams];
};

export const useRouteParams = (): RouteParams => useRouterContext().snapshot.params;

export const useResolvedPath = (
    to: NavigationTarget,
    options: { readonly relative?: 'path' | 'route' } = {},
): Readonly<Pick<AppLocation, 'hash' | 'pathname' | 'search'>> => {
    const router = useOptionalRouterContext();
    const level = useOptionalRouteLevelContext();
    const snapshot = router?.snapshot;
    const from =
        options.relative === 'path' || !level || !hasNavigationPathname(to)
            ? (snapshot?.location.pathname ?? '/')
            : (snapshot?.matches[level.index]?.pathnameBase ?? '/');
    return useMemo(() => {
        const resolved = resolvePath(to, from);
        const location = toLocation(resolved, {
            hash: '',
            key: '',
            pathname: from,
            search: '',
            state: null,
        });
        return {
            hash: location.hash,
            pathname: location.pathname,
            search: location.search,
        };
    }, [from, to]);
};
